import React, { useEffect, useRef, useState } from 'react';

import { NetworkService } from '@core/services/networkService/networkService';
import DemmiButton from '@demmi-ui/Button';
import DemmiIconButton, { IconButtonSize } from '@demmi-ui/IconButton';
import DemmiCardButtonArea from '@demmi-ui/Modal/ModalButtonArea';
import DemmiModalCore, { DemmiModalCoreRef } from '@demmi-ui/Modal/ModalCore';
import { demmiRequest } from '@helpers/app.helper';
import { ProductsHelper } from '@helpers/products.helper';
import { DemmiFS } from '@subhanhabib/demmilib';

import ModalCreateProductContent from './ModalCreateProductContent';

interface Props {
  onClose: () => void;
  onConfirm: (id: string) => void;
}

const ModalCreateProduct: React.FC<Props> = ({ onClose, onConfirm }) => {
  const CSSBlock = 'modal-create-product';
  const modalRef = useRef<DemmiModalCoreRef>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [product, setProduct] = useState<DemmiFS.Product>();

  /**
   * 0 = Doing nothing
   * 1 = Creating product
   * -1 = Failed to create
   */
  const [_, setState] = useState(0);

  useEffect(() => {
    if (product) setIsValid(ProductsHelper.isProductDetailsValid(product));
    else setIsValid(false);
  }, [product]);

  const [isValid, setIsValid] = useState(false);

  const gracefulExit = (postExit: () => void) => {
    modalRef.current ? modalRef.current.gracefulClose(postExit) : postExit();
    setTimeout(() => {
      setIsOpen(false);
    }, 500);
  };

  const pushProduct = async () => {
    if (isValid && product) {
      const { docID: _, ...newProduct } = product;
      demmiRequest(NetworkService.Product.createProduct(newProduct))
        .then(newID => {
          gracefulExit(() => onConfirm(newID));
        })
        .catch(() => setState(-1));
    }
  };

  const buttons = [
    <DemmiButton
      key={0}
      text="Discard"
      outlined
      onClick={() => gracefulExit(onClose)}
    />,
    <DemmiButton
      key={1}
      text="Create Product"
      disabled={!isValid}
      onClick={() => pushProduct()}
    />,
  ];

  return (
    <>
      <DemmiIconButton
        faIcon="fa-plus"
        onClick={() => setIsOpen(true)}
        size={IconButtonSize.SMALL}
      />
      <DemmiModalCore
        ref={modalRef}
        isOpen={isOpen}
        onExited={() => {
          setIsOpen(false);
          onClose();
        }}
        contentClassName={CSSBlock}
        allowBackdropClose={false}>
        <ModalCreateProductContent
          CSSBlock={CSSBlock}
          setProduct={setProduct}
        />
        <DemmiCardButtonArea buttons={buttons} />
      </DemmiModalCore>
    </>
  );
};

export default ModalCreateProduct;
